import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'
import { useSupportContacts } from '@context/SupportContactsContext'

import { Modal } from '@ui/components/Modal'
import { RadioProps } from '@ui/components/Radio'

import { NameDto } from '@model/NameDto'

import { useHomePageUrl } from '@hooks/useHomePageUrl'

interface Props {
  initialValue?: string
  isDemo?: boolean
  initialShowVipContent?: boolean
  accountTypes: NameDto<number>[]
  onSelect: (accountType: NameDto) => void
}

export const AccountTypeModal: BaseModalType<Props> = (props) => {
  const {
    close,
    onSelect,
    initialValue,
    accountTypes,
    isDemo,
    initialShowVipContent = false,
  } = props
  const { t } = useTranslation()
  const homePageUrl = useHomePageUrl()
  const { supportEmail } = useSupportContacts()

  const [selectedValue, setSelectedValue] = useState<string | undefined>(initialValue)
  const [showVipContent, setShowVipContent] = useState(initialShowVipContent)

  const handleSelection = (selectedId: string) => {
    setSelectedValue(selectedId)

    if (!selectedId) return

    const selected = accountTypes.find((at) => at.id.toString() === selectedId)
    if (!selected) return

    if (selected.name === 'VIP' && !isDemo) {
      setShowVipContent(true)
    } else {
      onSelect(selected)
      close()
    }
  }

  const handleVipAccept = () => {
    const proAccount = accountTypes.find((x) => x.name === 'Pro')
    if (proAccount) {
      onSelect(proAccount)
    }
    close()
  }

  const parsedOptions: RadioProps[] = accountTypes.map((at) => ({
    label: at.name,
    value: at.id.toString(),
  }))

  if (showVipContent) {
    return (
      <Modal>
        <Modal.Header>{t('Trading Account.Account Type')}</Modal.Header>
        <Modal.Body>
          <Modal.Body.Text>
            {t('Trading Account.Do you really want to open a VIP account')}
          </Modal.Body.Text>
          <Modal.Body.Text>
            {t(
              'Trading Account.NB! We will open a Pro account for you now. To change it to VIP, please follow the steps below:'
            )}
          </Modal.Body.Text>
          <Modal.Body.List>
            <Modal.Body.List.Item>
              {t('Trading Account.1. Reach balance of 50,000 (USD, EUR or GBP)')}
            </Modal.Body.List.Item>
            <Modal.Body.List.Item>
              {t(
                'Trading Account.2. Send an email to support@tickmill.com and request to upgrade your account from Pro to VIP.',
                { email: supportEmail }
              )}
            </Modal.Body.List.Item>
            <Modal.Body.List.Item>
              {t(
                'Trading Account.3. In case equity is less than 25,000 (USD, EUR or GBP) Tickmill reserves a right to change account type back to Pro.'
              )}
            </Modal.Body.List.Item>
          </Modal.Body.List>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Secondary onClick={close}>
            {t('Decline').toUpperCase()}
          </Modal.Footer.Secondary>
          <Modal.Footer.Primary onClick={handleVipAccept}>
            {t('Trading Account.Accept').toUpperCase()}
          </Modal.Footer.Primary>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal>
      <Modal.Header>{t('Trading Account.Account Type')}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Input.Radio
          value={selectedValue}
          name='account-type-radio'
          options={parsedOptions}
          onChange={handleSelection}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={() => {
            window.open(`${homePageUrl}/trading/accounts-overview`, '_blank')
          }}
        >
          {t('Learn More')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
